import { REFRESH_SOURCE_STATES } from './refreshTypes';
import type {
  RefreshAction,
  RefreshControl,
  RefreshImage,
  RefreshLanding,
  RefreshNote,
  RefreshPage,
  RefreshRestorePoint,
  RefreshSource,
  RefreshSourceState,
  RefreshStatus,
} from './refreshTypes';

/**
 * The Refresh health routes' wire, snake_case, to and from the client's types. Decoding is tolerant, as the other
 * segmentation decoders are: a field the client does not know is ignored, and a missing or mistyped one reads as
 * empty, never as an exception.
 */

type Wire = Record<string, unknown>;

const objectOf = (value: unknown): Wire => (typeof value === 'object' && value !== null ? (value as Wire) : {});

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

const textOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const count = (value: unknown): number => (typeof value === 'number' ? value : 0);

const texts = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

// Function declarations: Studio's tests read `.ts` as TSX, where `<T>(…) =>` parses as a tag.
function listOf<T>(value: unknown, decode: (wire: Wire) => T): T[] {
  return Array.isArray(value) ? value.map((entry) => decode(objectOf(entry))) : [];
}

function presentOr<T>(value: unknown, decode: (wire: Wire) => T): T | null {
  return typeof value === 'object' && value !== null ? decode(value as Wire) : null;
}

const noteOf = (wire: Wire): RefreshNote => ({ by: text(wire.by), note: text(wire.note), at: text(wire.at) });

const landingOf = (wire: Wire): RefreshLanding => ({
  snapshotId: text(wire.snapshot_id),
  committedAt: text(wire.committed_at),
});

const restorePointOf = (wire: Wire): RefreshRestorePoint => ({ ...landingOf(wire), liveRows: count(wire.live_rows) });

const imageOf = (wire: Wire): RefreshImage => ({ image: text(wire.image), copies: count(wire.copies) });

const stateOf = (value: unknown): RefreshSourceState =>
  REFRESH_SOURCE_STATES.includes(value as (typeof REFRESH_SOURCE_STATES)[number])
    ? (value as RefreshSourceState)
    : 'unknown';

const sourceOf = (wire: Wire): RefreshSource => ({
  table: text(wire.table),
  state: stateOf(wire.state),
  since: text(wire.since),
  cause: textOrNull(wire.cause),
  accepted: presentOr(wire.accepted, landingOf),
  earlierAccepted: listOf(wire.earlier_accepted, restorePointOf),
  held: presentOr(wire.held, landingOf),
  thinned: listOf(wire.thinned, (thin) => ({
    name: text(thin.name),
    count: count(thin.count),
    floor: typeof thin.floor === 'number' ? thin.floor : null,
  })),
  accept: textOrNull(wire.accept),
  quarantineReaders: presentOr(wire.quarantine_readers, (readers) => ({
    count: count(readers.count),
    segmentIds: texts(readers.segment_ids),
  })),
});

const statusOf = (wire: Wire): RefreshStatus => ({
  writtenAt: text(wire.written_at),
  image: textOrNull(wire.image),
  tables: Array.isArray(wire.tables) ? texts(wire.tables) : null,
  controlsUnreadable: presentOr(wire.controls_unreadable, (unreadable) => ({
    reason: text(unreadable.reason),
    at: text(unreadable.at),
  })),
  rebuilt: presentOr(wire.rebuilt, (rebuilt) => ({
    at: text(rebuilt.at),
    reason: text(rebuilt.reason),
    tablesRestored: rebuilt.tables_restored === true,
  })),
  images: Array.isArray(wire.images) ? listOf(wire.images, imageOf) : null,
  reseedsPending: presentOr(wire.reseeds_pending, (pending) => ({
    asOf: text(pending.as_of),
    segmentIds: texts(pending.segment_ids),
    segmentsTotal: count(pending.segments_total),
    images: listOf(pending.images, imageOf),
  })),
  sources: listOf(wire.sources, sourceOf),
  failing: listOf(wire.failing, (failure) => ({
    segmentId: text(failure.segment_id),
    errorCode: text(failure.error_code),
    failures: count(failure.failures),
    since: text(failure.since),
  })),
  failingTotal: count(wire.failing_total),
});

const controlOf = (wire: Wire): RefreshControl => ({
  table: text(wire.table),
  hold: presentOr(wire.hold, noteOf),
  accept: presentOr(wire.accept, (accept) => ({
    ...noteOf(accept),
    snapshotId: text(accept.snapshot_id),
    counts: texts(accept.counts),
  })),
  quarantine: presentOr(wire.quarantine, (quarantine) => ({
    ...noteOf(quarantine),
    landings: listOf(quarantine.landings, landingOf),
    restore: restorePointOf(objectOf(quarantine.restore)),
  })),
});

export function decodeRefreshPage(value: unknown): RefreshPage {
  const wire = objectOf(value);
  const controls = objectOf(wire.controls);
  const reseeds = objectOf(wire.reseeds);

  return {
    status: presentOr(wire.status, statusOf),
    controls: {
      version: textOrNull(controls.version),
      readable: controls.readable !== false,
      unreadable: presentOr(controls.unreadable, (unreadable) => ({
        reason: text(unreadable.reason),
        tables: Array.isArray(unreadable.tables)
          ? listOf(unreadable.tables, (entry) => ({ table: text(entry.table), reads: entry.reads === true }))
          : null,
        clearAllOnly: unreadable.clear_all_only === true,
      })),
      tables: listOf(controls.tables, controlOf),
    },
    reseeds: {
      version: textOrNull(reseeds.version),
      readable: reseeds.readable !== false,
      unreadableReason: textOrNull(reseeds.unreadable_reason),
      segments: listOf(reseeds.segments, (request) => ({ ...noteOf(request), segmentId: text(request.segment_id) })),
      images: listOf(reseeds.images, (request) => ({ ...noteOf(request), image: text(request.image) })),
    },
  };
}

const REFRESH = '/api/studio/v1/segments/refresh';

const sourcePath = (table: string, act: string) => `${REFRESH}/sources/${encodeURIComponent(table)}/${act}`;

export type RefreshRequest = { path: string; body: Record<string, unknown> };

/**
 * The route and body an act goes to. A note is sent only when there is one, as the routes' strict schemas expect.
 * Every kind has its own case, and the default fails the type check when a kind is added without one.
 */
export function encodeRefreshAction(action: RefreshAction): RefreshRequest {
  const note = action.note === undefined ? {} : { note: action.note };

  switch (action.kind) {
    case 'hold':
      return { path: sourcePath(action.table, 'hold'), body: { ...note, version: action.version } };
    case 'release':
      return { path: sourcePath(action.table, 'release'), body: { ...note, version: action.version } };
    case 'accept':
      return {
        path: sourcePath(action.table, 'accept'),
        body: {
          ...note,
          version: action.version,
          snapshot_id: action.snapshotId,
          ...(action.counts?.length ? { counts: action.counts } : {}),
        },
      };
    case 'quarantine':
      return {
        path: sourcePath(action.table, 'quarantine'),
        body: { ...note, version: action.version, snapshot_id: action.snapshotId },
      };
    case 'lift_quarantine':
      return { path: sourcePath(action.table, 'lift-quarantine'), body: { ...note, version: action.version } };
    case 'clear':
      return { path: sourcePath(action.table, 'clear'), body: { ...note, version: action.version } };
    case 'clear_all':
      return { path: `${REFRESH}/sources/clear-all`, body: { ...note, version: action.version } };
    case 'reseed_segment':
      return { path: `${REFRESH}/segments/${encodeURIComponent(action.segmentId)}/reseed`, body: note };
    case 'reseed_image':
      return { path: `${REFRESH}/images/reseed`, body: { ...note, image: action.image } };
    case 'clear_reseeds':
      return { path: `${REFRESH}/reseeds/clear`, body: { ...note, version: action.version } };
    default: {
      const unreachable: never = action;

      throw new Error(`Unknown refresh act: ${JSON.stringify(unreachable)}`);
    }
  }
}
